// Simple class-based theme controller for Tailwind dark mode (no next-themes)
import { useCallback, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

const THEME_KEY = 'theme';

export function getStoredTheme(): ThemeMode | null {
  try {
    const val = localStorage.getItem(THEME_KEY);
    return val === 'dark' || val === 'light' ? (val as ThemeMode) : null;
  } catch {
    return null;
  }
}

export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function preferredTheme(): ThemeMode {
  return getStoredTheme() ?? (systemPrefersDark() ? 'dark' : 'light');
}

export function applyTheme(theme: ThemeMode) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {}
}

// Call this as early as possible (in main.tsx) to avoid FOUC
export function initTheme() {
  if (typeof document === 'undefined') return;
  applyTheme(preferredTheme());
}

export function useThemeMode() {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.classList.contains('dark') ? 'dark' : preferredTheme();
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Sync when user changes system setting or localStorage in another tab
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onMedia = () => {
      if (!getStoredTheme()) {
        setThemeState(media.matches ? 'dark' : 'light');
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_KEY && (e.newValue === 'dark' || e.newValue === 'light')) {
        setThemeState(e.newValue as ThemeMode);
      }
    };
    media.addEventListener?.('change', onMedia);
    window.addEventListener('storage', onStorage);
    return () => {
      media.removeEventListener?.('change', onMedia);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setTheme = useCallback((next: ThemeMode) => setThemeState(next), []);
  const toggleTheme = useCallback(() => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')), []);
  const isDark = useMemo(() => theme === 'dark', [theme]);

  return { theme, isDark, setTheme, toggleTheme } as const;
}

